import { Component, Output, EventEmitter, OnDestroy, AfterViewInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CameraDevice, Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

@Component({
  selector: 'app-barcode-reader',
  templateUrl: './barcode-reader.component.html',
  styleUrls: ['./barcode-reader.component.css']
})
export class BarcodeReaderComponent implements AfterViewInit, OnDestroy {
  
  sound = new Audio('/assets/barcode.wav');
  barcodeValue: string = '';
  availableDevices: CameraDevice[] = [];
  currentDeviceId: string | null = null;

  hasDevices = false;
  hasPermission = false;
  qrResultString: string = '';

  html5QrCode!: Html5Qrcode;

  @Output() SaveEvent = new EventEmitter<string>();

  constructor(public dialogRef: MatDialogRef<BarcodeReaderComponent>) {}

  ngAfterViewInit() {
    this.checkCameraPermission();
  }

  logDebug(message: any) {
    console.log(message);
    const logElement = document.getElementById('debug-log');
    if (logElement) {
      const time = new Date().toLocaleTimeString();
      logElement.innerHTML += `<div>[${time}] ${message}</div>`;
      logElement.scrollTop = logElement.scrollHeight;
    }
  }

  async checkCameraPermission() {
    try {
      this.logDebug("Checking camera permission...");
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.hasPermission = true;
      stream.getTracks().forEach(track => track.stop());

      const devices = await Html5Qrcode.getCameras();
      this.availableDevices = devices;
      this.hasDevices = devices.length > 0;

      const rear = devices.find(d => /back|rear|environment/i.test(d.label));
      this.currentDeviceId = rear?.id || devices[0]?.id || null;

      if (this.currentDeviceId) {
        this.html5QrCode = new Html5Qrcode("reader");
        await this.startScanner(this.currentDeviceId);
      }
    } catch (error) {
      this.hasPermission = false;
      this.hasDevices = false;
      this.logDebug('Camera access denied or error: ' + error);
    }
  }

  async startScanner(deviceId: string) {
    if (!deviceId) return;
    this.logDebug("Attempting to start scanner with deviceId: " + deviceId);

    const config = {
      fps: 10,
      qrbox: { width: 300, height: 300 },
      formatsToSupport: [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.DATA_MATRIX,
        Html5QrcodeSupportedFormats.AZTEC,
        Html5QrcodeSupportedFormats.CODE_93,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.MAXICODE,
        Html5QrcodeSupportedFormats.PDF_417,
        Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
        Html5QrcodeSupportedFormats.CODABAR,
        Html5QrcodeSupportedFormats.RSS_14,
        Html5QrcodeSupportedFormats.RSS_EXPANDED,
        Html5QrcodeSupportedFormats.UPC_E,
      ]
    };

    this.html5QrCode = new Html5Qrcode("reader");

    try {
      await Promise.race([
        this.html5QrCode.start(
          { deviceId: { exact: deviceId } },
          config,
          this.onScanSuccess.bind(this),
          this.onScanError.bind(this)
        ),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Camera start timeout")), 8000))
      ]);
    } catch (err) {
      this.logDebug(`Primary start failed: ${err}`);
      await this.tryFallback(config);
    }
  }

  private async tryFallback(config: any) {
    try {
      await this.html5QrCode.stop().catch(e => this.logDebug(`Stop before fallback error: ${e}`));
      await this.html5QrCode.clear();
      this.logDebug(`Trying fallback facingMode: environment...`);
      await this.html5QrCode.start(
        { facingMode: "environment" },
        config,
        this.onScanSuccess.bind(this),
        this.onScanError.bind(this)
      );
    } catch (fallbackErr) {
      this.logDebug(`Fallback facingMode failed: ${fallbackErr}`);
      await this.tryFinalFallback(config);
    }
  }

private async tryFinalFallback(config: any) {
  try {
    await this.html5QrCode.stop().catch(e => this.logDebug(`Stop before final fallback error: ${e}`));
    await this.html5QrCode.clear();

    if (this.availableDevices.length > 0) {
      const firstDeviceId = this.availableDevices[0].id;
      this.logDebug(`Trying final fallback with first available camera: ${firstDeviceId}`);
      await this.html5QrCode.start(
        { deviceId: { exact: firstDeviceId } },
        config,
        this.onScanSuccess.bind(this),
        this.onScanError.bind(this)
      );
    } else {
      this.logDebug("No available devices found for final fallback.");
    }
  } catch (finalErr) {
    this.logDebug(`All start attempts failed: ${finalErr}`);
  }
}

  onScanSuccess(decodedText: string) {
    this.logDebug(`Decoded: ${decodedText}`);
    if (this.sound) this.sound.play();
    this.qrResultString = this.barcodeValue = decodedText;
    this.SaveEvent.emit(decodedText);
    this.dialogRef.close(true);
  }

  onScanError(errorMessage: string) {
    // Ignore common "no code found" messages to reduce spam
    if (!errorMessage.includes("NotFoundException")) {
      this.logDebug(`Decode error: ${errorMessage}`);
    }
  }

  async onDeviceSelectChange(selected: string) {
    this.currentDeviceId = selected;
    this.logDebug(`Switching to camera: ${selected}`);
    await this.stopScanner();
    this.html5QrCode = new Html5Qrcode("reader"); // New instance for stability
    await this.startScanner(selected);
  }

  async stopScanner() {
    if (this.html5QrCode) {
      try {
        await this.html5QrCode.stop();
        await this.html5QrCode.clear();
      } catch (e) {
        this.logDebug(`Stop scanner error: ${e}`);
      }
    }
  }

  ngOnDestroy(): void {
    this.stopScanner();
  }
}
